// Copied verbatim from eval/dashboard/internal_web/src/components/ui/Button.tsx (ATM-57). Keep in step until ATM-60 shares it.
/**
 * Every button in the app, in four variants, so a control cannot be invented at a
 * call site.
 *
 * - `primary` — the dark pill a card is actually asking for ("Save", "Got it"). At most
 *   one per card.
 * - `outline` — the quiet sibling ("View All", "Review", "Learn more"): a white box with
 *   a hairline edge that warms to the design's pale yellow on hover.
 * - `accent` — the one BLUE filled control ("Add", "Comment"). It is blue for a
 *   reason worth keeping: it has to be findable against arbitrary transcript prose,
 *   where a dark pill disappears into the text it is annotating.
 * - `link` — a bare text action ("Cancel", "Edit", "Remove").
 *
 * Sizes are two: 40px for a form action, 32px for one inside a bubble or a card heading.
 * The classes live in `buttonStyles`, shared with the links drawn as buttons.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "../../lib/cn";
import { buttonClass, type ButtonSize, type ButtonVariant } from "./buttonStyles";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
};

/** A button. `type` defaults to `button` — a stray form submit is never wanted here. */
export default function Button({
  variant = "primary",
  size = "md",
  className,
  type = "button",
  children,
  ...rest
}: Props) {
  return (
    <button type={type} className={cn(buttonClass(variant, size), className)} {...rest}>
      {children}
    </button>
  );
}
